import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Stack, Chip, Table, TableBody,
  TableCell, TableHead, TableRow, TableContainer, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Alert, IconButton, Grid, Tooltip,
} from '@mui/material';
import { Description, Add, Delete, Refresh, ContentCopy, FileCopy } from '@mui/icons-material';
import { api } from '../config/api';
import { PLANTILLAS_WHATSAPP, PLANTILLAS_POR_INDUSTRIA } from '../content/plantillasWhatsapp';

const estadoColor: Record<string, any> = { APPROVED: 'success', PENDING: 'warning', REJECTED: 'error' };

const Plantillas: React.FC = () => {
  const [plantillas, setPlantillas] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: '', category: 'MARKETING', language: 'es', body: '' });
  const [tab, setTab] = useState<'meta' | 'whatsapp'>('whatsapp'); // Mostrar pestañas
  const [dlgPlantilla, setDlgPlantilla] = useState<any>(null); // Para ver detalle de plantilla predefinida
  const [copiarMsg, setCopiarMsg] = useState<string | null>(null);

  const cargar = () => {
    setLoading(true);
    setError('');
    api.get('/meta/plantillas')
      .then((r) => setPlantillas(r.data.plantillas || []))
      .catch((e) => setError(e.response?.data?.error || 'No se pudieron cargar las plantillas (verifica tus credenciales Meta)'))
      .finally(() => setLoading(false));
  };
  useEffect(cargar, []);

  const crear = async () => {
    setError('');
    try {
      await api.post('/meta/plantillas', {
        name: form.name.toLowerCase().replace(/[^a-z0-9_]/g, '_'),
        category: form.category,
        language: form.language,
        components: [{ type: 'BODY', text: form.body }],
      });
      setOpen(false);
      setForm({ name: '', category: 'MARKETING', language: 'es', body: '' });
      cargar();
    } catch (e: any) {
      setError(e.response?.data?.error || 'Error creando plantilla');
    }
  };

  const eliminar = async (nombre: string) => {
    await api.delete(`/meta/plantillas/${nombre}`).catch(() => {});
    cargar();
  };;

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Description color="primary" />
          <Typography variant="h4">Plantillas</Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          {tab === 'meta' && (
            <>
              <IconButton onClick={cargar}><Refresh /></IconButton>
              <Button variant="contained" startIcon={<Add />} onClick={() => setOpen(true)}>Nueva plantilla Meta</Button>
            </>
          )}
        </Stack>
      </Stack>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        {tab === 'whatsapp'
          ? '📋 Coppa estos mensajes predefinidos a tu base de conocimiento del agente y personaliza con tu estilo.'
          : 'Crea y gestiona tus plantillas de Meta directamente desde aquí.'}
      </Typography>

      {/* Tabs */}
      <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
        <Chip
          label="📱 Plantillas WhatsApp"
          onClick={() => setTab('whatsapp')}
          color={tab === 'whatsapp' ? 'primary' : 'default'}
          variant={tab === 'whatsapp' ? 'filled' : 'outlined'}
        />
        <Chip
          label="📋 Plantillas Meta"
          onClick={() => setTab('meta')}
          color={tab === 'meta' ? 'primary' : 'default'}
          variant={tab === 'meta' ? 'filled' : 'outlined'}
        />
      </Stack>

      {/* PESTAÑA: Plantillas WhatsApp Predefinidas */}
      {tab === 'whatsapp' && (
        <>
          <Grid container spacing={2}>
            {Object.entries(PLANTILLAS_POR_INDUSTRIA()).map(([industria, plantillas_ind]) => (
              <Grid item xs={12} key={industria}>
                <Card sx={{ border: '1px solid #E2E8F0' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                      {plantillas_ind[0]?.emoji} {industria}
                    </Typography>
                    <Stack spacing={1.5}>
                      {plantillas_ind.map((p) => (
                        <Box
                          key={p.id}
                          sx={{
                            p: 1.5,
                            border: '1px solid #E2E8F0',
                            borderRadius: 1,
                            bgcolor: '#F8FAFC',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': { bgcolor: '#F1F5F9', borderColor: '#CBD5E1' },
                          }}
                          onClick={() => setDlgPlantilla(p)}
                        >
                          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                            <Box>
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Chip size="small" label={p.categoria} variant="outlined" />
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                  {p.nombre}
                                </Typography>
                              </Stack>
                              <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                {p.mensaje}
                              </Typography>
                            </Box>
                            <Tooltip title="Copiar al portapapeles">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigator.clipboard.writeText(p.mensaje);
                                  setCopiarMsg(p.id);
                                  setTimeout(() => setCopiarMsg(null), 2000);
                                }}
                              >
                                {copiarMsg === p.id ? '✅' : <ContentCopy fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Dialog: Ver plantilla completa */}
          <Dialog open={!!dlgPlantilla} onClose={() => setDlgPlantilla(null)} fullWidth maxWidth="sm">
            <DialogTitle>{dlgPlantilla?.nombre}</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <Box>
                  <Typography variant="overline" color="text.secondary">Categoría</Typography>
                  <Chip size="small" label={dlgPlantilla?.categoria} />
                </Box>
                <Box>
                  <Typography variant="overline" color="text.secondary">Mensaje</Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    value={dlgPlantilla?.mensaje || ''}
                    InputProps={{ readOnly: true }}
                    sx={{ mt: 1 }}
                  />
                </Box>
                {dlgPlantilla?.variables && dlgPlantilla.variables.length > 0 && (
                  <Box>
                    <Typography variant="overline" color="text.secondary">Variables a personalizar</Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {dlgPlantilla.variables.join(', ')}
                    </Typography>
                  </Box>
                )}
                <Alert severity="info">
                  💡 Copia este mensaje, personalízalo con tus datos y agrégalo a la base de conocimiento de tu agente.
                </Alert>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDlgPlantilla(null)}>Cerrar</Button>
              <Button
                variant="contained"
                startIcon={<ContentCopy />}
                onClick={() => {
                  navigator.clipboard.writeText(dlgPlantilla?.mensaje || '');
                  setDlgPlantilla(null);
                }}
              >
                Copiar mensaje
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      {/* PESTAÑA: Plantillas Meta */}
      {tab === 'meta' && (
        <>
          {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

          <Card>
            <CardContent>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Nombre</TableCell>
                      <TableCell>Categoría</TableCell>
                      <TableCell>Idioma</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell align="right">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {plantillas.map((p) => (
                      <TableRow key={p.id || p.name}>
                        <TableCell>{p.name}</TableCell>
                        <TableCell>{p.category}</TableCell>
                        <TableCell>{p.language}</TableCell>
                        <TableCell><Chip size="small" label={p.status} color={estadoColor[p.status] || 'default'} /></TableCell>
                        <TableCell align="right">
                          <IconButton size="small" color="error" onClick={() => eliminar(p.name)}><Delete fontSize="small" /></IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {!loading && plantillas.length === 0 && (
                      <TableRow><TableCell colSpan={5} align="center" sx={{ color: 'text.secondary', py: 4 }}>Sin plantillas Meta aún</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
            <DialogTitle>Nueva plantilla Meta</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <TextField label="Nombre (sin espacios)" fullWidth value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                <TextField select label="Categoría" fullWidth value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                  <MenuItem value="MARKETING">Marketing</MenuItem>
                  <MenuItem value="UTILITY">Utilidad</MenuItem>
                  <MenuItem value="AUTHENTICATION">Autenticación</MenuItem>
                </TextField>
                <TextField select label="Idioma" fullWidth value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })}>
                  <MenuItem value="es">Español</MenuItem>
                  <MenuItem value="en">Inglés</MenuItem>
                  <MenuItem value="pt_BR">Portugués</MenuItem>
                </TextField>
                <TextField label="Texto del mensaje" fullWidth multiline rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpen(false)}>Cancelar</Button>
              <Button variant="contained" onClick={crear} disabled={!form.name || !form.body}>Crear</Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
};

export default Plantillas;
